import React, { useState, useEffect, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import cn from "classnames";
import styles from "./StudentDetail.module.sass";
import { Link } from "react-router-dom";
import Icon from "../../components/Icon";
import { useAuth } from "../../contexts/AuthContext";

// Screens
import NameAndDescription from "./NameAndDescription";
import MemosAndNotes from "./MemosAndNotes";

// Components  
import Card from "../../components/Card";
import Dropdown from "../../components/Dropdown";
import Actions from "../../components/Actions";

// Services
import { getProspectCompanyById, assignCommercialToProspectCompany } from "../../services/prospectCompany.service";
import { getCommercials } from "../../services/prospect.service";
import { convertProspectToCompany } from "../../services/createCompany.service";
import { showSuccess, showError } from "../../services/toast.service";
import { isAdmin } from "../../services/pocketbase";

// Options pour le menu d'actions
const actions = [
  {
    title: "Créer une entreprise",
    action: () => {} // Sera définie dans le composant
  }
];
const navigation = ["Coordonnées", "Historique"];

const ProspectCompanyDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [activeIndex, setActiveIndex] = useState(0);
  
  // Debug: vérifier le rôle de l'utilisateur
  console.log('Current user:', currentUser);
  console.log('User role:', currentUser?.role);
  console.log('Is Admin (manual check)?:', currentUser?.role?.includes?.('Admin') || currentUser?.role === 'Admin');
  console.log('Is Admin (pocketbase function)?:', isAdmin());
  const [company, setCompany] = useState(location.state?.prospectCompany || null);
  const [loading, setLoading] = useState(!location.state?.prospectCompany);
  const [error, setError] = useState(null);
  const [commercials, setCommercials] = useState([]);
  const [assigningCommercial, setAssigningCommercial] = useState(false);
  const [convertingCompany, setConvertingCompany] = useState(false);

  // Fonction pour rafraîchir les données de l'entreprise prospect
  const refreshCompany = useCallback(async () => {
    try {
      if (id) {
        const companyData = await getProspectCompanyById(id);
        setCompany(companyData);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'entreprise prospect:', error);
    }
  }, [id]);

  // Récupérer la liste des commerciaux
  useEffect(() => {
    const fetchCommercials = async () => {
      try {
        const commercialsData = await getCommercials();
        setCommercials(commercialsData);
      } catch (error) {
        console.error('Erreur lors de la récupération des commerciaux:', error);
      }
    };

    fetchCommercials();
  }, []);

  // Fonction pour assigner un commercial
  const assignCommercial = async (commercialId) => {
    try {
      setAssigningCommercial(true);
      const commercial = commercials.find(c => c.id === commercialId);
      const isReassignment = company.expand?.assigned_to;
      
      await assignCommercialToProspectCompany(company.id, commercialId);
      await refreshCompany();
      
      if (commercial) {
        const message = isReassignment 
          ? `L'entreprise prospect a été réassignée à ${commercial.first_name} ${commercial.last_name}`
          : `L'entreprise prospect a été assignée à ${commercial.first_name} ${commercial.last_name}`;
        showSuccess(message);
      }
      
      setAssigningCommercial(false);
    } catch (error) {
      console.error('Erreur lors de l\'assignation du commercial:', error);
      showError('Erreur lors de l\'assignation du commercial');
      setAssigningCommercial(false);
    }
  };

  const handleConvertToCompany = async () => {
    try {
      // Vérification des champs obligatoires
      if (!company.name || !company.representative_email) {
        showError('L\'entreprise prospect doit avoir un nom et un email représentant pour être convertie en entreprise.');
        return;
      }

      setConvertingCompany(true);
      const result = await convertProspectToCompany(company.id);

      await refreshCompany();
      showSuccess(`L'entreprise ${result.company.name} a été créée avec succès!`);
      setConvertingCompany(false);
    } catch (error) {
      console.error('Erreur lors de la conversion de l\'entreprise prospect:', error);
      showError(error.message);
      setConvertingCompany(false);
    }
  };

  // Définition des actions pour le composant Actions
  const actionsWithHandlers = [
    {
      title: "Créer une entreprise",
      action: () => handleConvertToCompany()
    }
  ];

  // Récupérer l'entreprise depuis l'API si elle n'est pas déjà disponible
  useEffect(() => {
    // Si nous avons déjà l'entreprise prospect depuis la navigation, ne pas la récupérer à nouveau
    if (location.state?.prospectCompany) return;
    
    const fetchCompany = async () => {
      setLoading(true);
      setError(null);
      try {
        const companyData = await getProspectCompanyById(id);
        if (companyData) {
          setCompany(companyData);
        } else {
          setError("Entreprise prospect non trouvée");
          setTimeout(() => navigate("/admin/prospects_company/"), 2000);
        }
      } catch (err) {
        console.error("Erreur lors de la récupération de l'entreprise prospect:", err);
        setError("Impossible de charger les détails de l'entreprise prospect");
        setTimeout(() => navigate("/admin/prospects_company/"), 2000);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchCompany();
    } else {
      setError("ID d'entreprise prospect non valide");
      setTimeout(() => navigate("/admin/prospects_company/"), 2000);
    }
  }, [id, navigate, location.state]);


  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loading}>Chargement des détails de l'entreprise prospect...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.error}>{error}</div>
        <div>Redirection vers la liste des entreprises prospects...</div>
      </div>
    );
  }

  if (!company) {
    return null;
  }

  return (
    <>
      <div className={styles.shop}>


        <Link className={cn("button-white", styles.button)} to="/admin/prospects_company">
          <Icon name="arrow-left" />
          Retour
        </Link>

        <div className={styles.background}>
          <img src="/images/content/bg-shop.jpg" alt="Background" />
        </div>

        <Card className={styles.card}>

          {/* Profile */}

          <div className={styles.profile}>
            <div className={styles.details}>
              <div className={styles.avatar}>
                <img src={company.logoUrl || '/images/content/company_logo.png'} alt="Avatar" />
              </div>
              <div className={styles.wrap}>
                <div className={cn("h4", styles.man)}>{company.name || 'Entreprise'}</div>
                <div className={styles.login}>{company.sector || 'Non spécifié'}</div>
              </div>
            </div>
            <div className={styles.statusContainer}>
            <div className={styles.statusGroup}>
                <div className={styles.login}>Assigné à</div>
                {company.expand?.assigned_to && !isAdmin() ? (
                  <div className={cn(styles.statusBadge, 'status-type', 'status-blue-dark')}>
                    {company.expand.assigned_to.first_name} {company.expand.assigned_to.last_name}
                  </div>
                ) : (
                  <div className={styles.assignCommercial}>
                    {assigningCommercial ? (
                      <div className={cn(styles.statusBadge, 'status-type', 'status-gray-dark')}>
                        {company.expand?.assigned_to ? 'Changement...' : 'Assignation...'}
                      </div>
                    ) : (
                      <Dropdown
                        className={styles.dropdown}
                        value={company.expand?.assigned_to ? 
                          `${company.expand.assigned_to.first_name} ${company.expand.assigned_to.last_name}` : 
                          "Assigner un commercial"
                        }
                        setValue={(value) => {
                          const commercial = commercials.find(c => `${c.first_name} ${c.last_name}` === value);
                          if (commercial) {
                            assignCommercial(commercial.id);
                          }
                        }}
                        options={commercials.map(commercial => `${commercial.first_name} ${commercial.last_name}`)}
                      />
                    )}
                  </div>
                )}
              </div>
              <div className={styles.statusGroup}>
                <div className={styles.login}>Taille</div>
                <div className={cn(
                  styles.statusBadge,
                  'status-type',
                  'status-green-dark'
                )}>
                  {company.company_size || 'Non spécifié'}
                </div>
              </div>

              <div className={styles.statusGroup}>
                <div className={styles.login}>Statut</div>
                <div className={cn(
                  styles.statusBadge,
                  'status-type',
                  company.status === 'Actif' ? 'status-green-dark' : 'status-gray-dark'
                )}>
                  {company.status || 'Non spécifié'}
                </div>
              </div>

            </div>
          </div>
          
          <div className={styles.control}>
            <div className={cn(styles.nav, styles.desktopNav)}>
              {navigation.map((x, index) => (
                <button
                  className={cn(styles.link, {
                    [styles.active]: index === activeIndex,
                  })}
                  onClick={() => setActiveIndex(index)}
                  key={index}
                >
                  {x}
                </button>
              ))}
            </div>
            
            <div className={styles.actionContainer}>
              {convertingCompany ? (
                <div className={styles.loading}>Conversion en cours...</div>
              ) : (
                <Actions
                  className={styles.actions}
                  classActionsHead={styles.actionsHead}
                  items={actionsWithHandlers}
                  small
                />
              )}
            </div>

            {/* Mobile Navigation */}
            <div className={styles.mobileNav}>
              <Dropdown
                className={styles.dropdown}
                value={navigation[activeIndex]}
                setValue={(value) => {
                  const index = navigation.findIndex(x => x === value);
                  if (index !== -1) setActiveIndex(index);
                }}
                options={navigation}
              />
            </div>
          </div>

        </Card>

        <div className={styles.wrap}>
          {activeIndex === 0 && (
            <>
              <NameAndDescription company={company} />
            </>
          )}
          {activeIndex === 1 && (
            <>
               <MemosAndNotes prospect={company} onStatusUpdate={refreshCompany}/>
            </>
          )}      
        </div>
      </div>
    </>
  );
};

export default ProspectCompanyDetail;
